import { useCallback, useEffect, useState } from "react";
import { ACTIVE_CUSTOMER } from "@constants";
import { IUserContactPermissions, TPermissionGroup } from "../../interface/IUserDetails";
import { getContactPermissionRoute, updateContactPermissionRoute } from "@api-routes/contacts";
import {
    getResponseToState,
    getStateToPayload,
    IUserPermissionsGroupResponse,
} from "../mappers/getContactPreferencesMapper";
import { useUserContext } from "@hook/useUserContext";
import { useFetch } from "@common/hook/useFetch";

export const useContactPreferences = () => {
    const [userContactPermissions, setUserContactPermissions] = useState<IUserContactPermissions[]>([]);
    const [permissionGroup, setPermissionGroup] = useState<TPermissionGroup>(ACTIVE_CUSTOMER);
    const [getContactPreferenceListState, { get }] = useFetch();
    const [updateContactPreferenceListState, { put: putContactPermissions }] = useFetch();
    const { username: contactUuid } = useUserContext();

    const onUserPermissionsResponse = (userPermissionsResponse?: IUserPermissionsGroupResponse) => {
        if (userPermissionsResponse) {
            const userPermissionsMapped = getResponseToState(userPermissionsResponse);

            if (userPermissionsMapped.length > 0) {
                setUserContactPermissions(userPermissionsMapped[0].permissions);
            }
        }
    };

    const getContactPreference = useCallback(() => {
        if (contactUuid) {
            get<IUserPermissionsGroupResponse>(getContactPermissionRoute(permissionGroup, contactUuid)).then(
                onUserPermissionsResponse
            );
        }
    }, [contactUuid, get, permissionGroup]);

    useEffect(getContactPreference, [getContactPreference, permissionGroup]);

    const handleChange = (contactPermission: string): void => {
        const newUserContactPermissions = userContactPermissions.map((permission) =>
            permission.permission === contactPermission
                ? { ...permission, isEnabled: !permission.isEnabled }
                : { ...permission }
        );

        setUserContactPermissions(newUserContactPermissions);
    };

    const updateContactPreferences = () => {
        if (contactUuid) {
            putContactPermissions<IUserPermissionsGroupResponse>(updateContactPermissionRoute(contactUuid), {
                body: getStateToPayload(permissionGroup, userContactPermissions),
            }).then(onUserPermissionsResponse);
        }
    };

    return {
        getContactPreference,
        getContactPreferenceListState,
        handleChange,
        permissionGroup,
        setPermissionGroup,
        updateContactPreferenceListState,
        updateContactPreferences,
        userContactPermissions,
    };
};
